import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { formatMoney } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { ConfirmDialog } from '../components/Modal';
import { IconPencil, IconTrash, IconStar, IconFlame, IconLeaf } from '../components/Icons';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

const GRID_PAGE_SIZE = 12;

function rowId(r) {
  return r.menuItemId || r.id;
}

function ratingText(r) {
  const v = r.averageRating ?? r.avgRating ?? r.rating;
  if (v == null || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toFixed(1);
}

function reviewCountText(r) {
  const v = r.reviewCount ?? r.reviewsCount ?? r.reviewTotal;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function truncateDescription(text, maxLen = 120) {
  const s = String(text || '').trim();
  if (!s) return '';
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen).trim()}…`;
}

export default function MenuItems() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [nameContains, setNameContains] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState(null);

  const categoryById = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => {
      const id = c.categoryId || c.id;
      if (id != null) m.set(String(id), c.name || '—');
    });
    return m;
  }, [categories]);

  const loadCategories = useCallback(async () => {
    try {
      const raw = await adminApi.listCategories({ skip: 0, limit: 200 });
      const norm = normalizeListResponse(raw, 200);
      setCategories(norm.items);
    } catch {
      /* ignore */
    }
  }, []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip: 0, limit: GRID_PAGE_SIZE };
      if (categoryId) params.categoryId = categoryId;
      if (nameContains.trim()) params.nameContains = nameContains.trim();
      const raw = await adminApi.listMenuItems(params);
      const norm = normalizeListResponse(raw, GRID_PAGE_SIZE);
      setItems(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [categoryId, nameContains]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (items.length >= totalCount || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const params = { skip: items.length, limit: GRID_PAGE_SIZE };
      if (categoryId) params.categoryId = categoryId;
      if (nameContains.trim()) params.nameContains = nameContains.trim();
      const raw = await adminApi.listMenuItems(params);
      const norm = normalizeListResponse(raw, GRID_PAGE_SIZE);
      setItems((prev) => [...prev, ...norm.items]);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  }, [items.length, totalCount, loadingMore, categoryId, nameContains]);

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteMenuItem(deleteId);
      setDeleteId(null);
      await loadInitial();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const hasMore = items.length < totalCount;

  async function handleToggleActive(r, checked) {
    const id = rowId(r);
    if (id == null) return;
    setToggleBusyId(id);
    setItems((prev) => prev.map((x) => (String(rowId(x)) === String(id) ? { ...x, isActive: checked } : x)));
    try {
      await adminApi.updateMenuItem(id, { isActive: checked });
    } catch (err) {
      setError(pickErrorMessage(err));
      setItems((prev) => prev.map((x) => (String(rowId(x)) === String(id) ? { ...x, isActive: !checked } : x)));
    } finally {
      setToggleBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Menu items"
        subtitle="Dishes and pricing"
        actions={
          <Link to="/menu-items/new" className="btn btn-primary">
            New menu item
          </Link>
        }
      />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small mb-1">Search name</label>
              <input
                className="form-control"
                value={nameContains}
                onChange={(e) => setNameContains(e.target.value)}
                placeholder="Contains…"
              />
            </div>
            <div className="col-md-5">
              <label className="form-label small mb-1">Category</label>
              <select className="form-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c.categoryId || c.id} value={c.categoryId || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={() => loadInitial()} />
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-3 p-md-4">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : items.length === 0 ? (
            <div className="menu-items-grid-empty text-center py-5 rounded-3">
              <p className="text-secondary mb-3">No dishes match these filters.</p>
              <Link to="/menu-items/new" className="btn btn-sm btn-outline-primary">
                New menu item
              </Link>
            </div>
          ) : (
            <>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                {items.map((r, index) => {
                  const id = rowId(r);
                  const catName = categoryById.get(String(r.categoryId)) ?? r.categoryId ?? '—';
                  const rate = ratingText(r);
                  const reviews = reviewCountText(r);
                  const desc = truncateDescription(r.description);
                  return (
                    <div className="col" key={String(id ?? `row-${index}`)}>
                      <article className="menu-item-app-card h-100 d-flex flex-column">
                        <div className="menu-item-app-card__media">
                          {r.imageUrl ? (
                            <img src={r.imageUrl} alt="" className="menu-item-app-card__img" loading="lazy" />
                          ) : (
                            <div className="menu-item-app-card__img menu-item-app-card__img--placeholder" aria-hidden>
                              <span className="menu-item-app-card__placeholder-icon">?</span>
                            </div>
                          )}
                          <div className="menu-item-app-card__badges">
                            {r.isPopular ? (
                              <span className="menu-item-app-card__badge menu-item-app-card__badge--fire" title="Popular">
                                <IconFlame size={14} strokeWidth={2} />
                              </span>
                            ) : null}
                            {r.isFeatured ? (
                              <span className="menu-item-app-card__badge menu-item-app-card__badge--star" title="Featured">
                                <IconStar size={13} strokeWidth={2} />
                              </span>
                            ) : null}
                          </div>
                          <div className="menu-item-app-card__admin-actions">
                            <Link
                              to={`/menu-items/${id}`}
                              className="menu-item-app-card__icon-btn"
                              title="Edit"
                              aria-label="Edit"
                            >
                              <IconPencil size={16} />
                            </Link>
                            <button
                              type="button"
                              className="menu-item-app-card__icon-btn"
                              onClick={() => setDeleteId(id)}
                              title="Delete"
                              aria-label="Delete"
                            >
                              <IconTrash size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="menu-item-app-card__body flex-grow-1 d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <h3 className="menu-item-app-card__title mb-0">{r.name || 'Untitled'}</h3>
                            <span className="menu-item-app-card__price flex-shrink-0">{formatMoney(r.price)}</span>
                          </div>
                          {desc ? <p className="menu-item-app-card__desc flex-grow-1">{desc}</p> : <p className="menu-item-app-card__desc menu-item-app-card__desc--empty">No description</p>}
                          <div className="menu-item-app-card__footer mt-auto">
                            <div className="menu-item-app-card__active-row mt-2">
                              <label className="menu-item-app-card__active-label mb-0" htmlFor={`menu-active-${id}`}>
                                Active
                              </label>
                              <div
                                className="form-check form-switch mb-0 admin-category-switch menu-item-app-card__active-switch"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <input
                                  id={`menu-active-${id}`}
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={Boolean(r.isActive)}
                                  disabled={String(toggleBusyId) === String(id)}
                                  onChange={(e) => handleToggleActive(r, e.target.checked)}
                                  aria-label={`${r.name || 'Menu item'} — active in catalog`}
                                />
                              </div>
                            </div>
                            <div className="menu-item-app-card__meta pt-2 d-flex flex-wrap align-items-center gap-2">
                              {rate != null ? (
                                <span className="menu-item-app-card__rating">
                                  <IconStar size={15} className="menu-item-app-card__rating-star" strokeWidth={2} />
                                  <span className="menu-item-app-card__rating-num">{rate}</span>
                                  {reviews != null ? (
                                    <span className="menu-item-app-card__rating-count">({reviews})</span>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="menu-item-app-card__rating menu-item-app-card__rating--empty">No rating yet</span>
                              )}
                              {r.isVegetarian ? (
                                <span className="menu-item-app-card__veg">
                                  <IconLeaf size={14} strokeWidth={2} />
                                  Veg
                                </span>
                              ) : null}
                              <span className="menu-item-app-card__cat ms-auto text-truncate" title={catName}>
                                {catName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>
                    </div>
                  );
                })}
              </div>

              {hasMore ? (
                <div className="text-center pt-4">
                  <button type="button" className="btn menu-items-load-more" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      {!loading && items.length > 0 && !hasMore ? (
        <p className="menu-items-list-end-note">All items loaded</p>
      ) : null}

      {deleteId ? (
        <ConfirmDialog
          title="Delete menu item"
          message="This removes the dish from the catalog. Continue?"
          confirmLabel="Delete"
          danger
          busy={deleting}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}
